"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CustomSubmittalSupport() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const offset = (window.innerHeight - rect.top) * 0.12;
      setScrollY(offset);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionRef]);
  const metadata = [{
    label: "REVIEW TIME",
    value: "2–3 DAYS"
  }, {
    label: "STARTING AT",
    value: "$150"
  }, {
    label: "ACCURACY",
    value: "100% COMPLIANT"
  }];
  const paragraphs = ["Standard documentation often lacks the technical rigor required for complex industrial projects. We bridge the gap between engineering intent and regulatory reality, ensuring every submittal is structured for immediate approval.", "Our process follows a strict three-tier verification: initial compliance audit, technical data validation, and final formatting for client submission. Each document is reviewed with the precision of a firm that understands the cost of delays.", "The result is not just a completed file. It is a seamless transition — from design to procurement, to the quiet confidence of a project that moves forward without friction."];
  return <section data-section-id="4195" id="custom-submittal-support" ref={sectionRef} className="bg-background text-foreground overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {}
        <div className="relative overflow-hidden h-[60vh] lg:h-auto min-h-[500px]">
          <div className="absolute inset-0 w-full h-[120%] -top-[10%]" style={{
          transform: `translate3d(0, ${scrollY}px, 0)`,
          willChange: "transform"
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_submittal_review.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_submittal_review.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/technical_submittal_review.png-wvc-sizes" alt="Professional reviewing technical engineering documents in a modern office" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-background/30" />
          </div>
        </div>

        {}
        <div className="relative flex items-center bg-card px-6 py-16 sm:px-10 sm:py-20 lg:px-[120px] lg:py-32">
          <div className="w-full max-w-xl">
            {}
            <div className={`font-mono text-xs tracking-[0.2em] text-primary uppercase mb-8 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              04 — Technical Support
            </div>

            {}
            <h2 className={`font-default text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground mb-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? "200ms" : "0ms"
          }}>
              A standard submittal is not a <em className="italic font-light text-primary">solution</em>.
            </h2>

            {}
            <div className="space-y-5 mb-12">
              {paragraphs.map((paragraph, i) => <p data-index={i} key={i} className={`font-default text-base leading-relaxed text-muted-foreground transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: isVisible ? `${400 + i * 200}ms` : "0ms"
            }}>
                  {paragraph}
                </p>)}
            </div>

            {}
            <div className={`grid grid-cols-1 sm:grid-cols-3 border-t border-b border-border py-6 mb-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? "1000ms" : "0ms"
          }}>
              {metadata.map((item, i) => <div data-index={i} key={i} className={`flex flex-col gap-2 px-0 sm:px-4 py-3 sm:py-0 ${i > 0 ? "border-t sm:border-t-0 sm:border-l border-border" : ""}`}>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
                    {item.label}
                  </span>
                  <span className="font-mono text-sm tracking-wider text-foreground uppercase">
                    {item.value}
                  </span>
                </div>)}
            </div>

            {}
            <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? "1200ms" : "0ms"
          }}>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.2em] uppercase h-12 px-8 rounded-lg">
                <Link to="/ServicesPage">
                  Request Support
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}